import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Paperclip, Download, Trash2, Pencil, X, FileText, ArrowLeftRight } from "lucide-react";
import { fmtDate } from "@/lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function euro(n) { return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0); }
const todayISO = () => new Date().toLocaleDateString("en-CA");

export default function Movimenti() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ date_from: "", date_to: "", type: "all", category_id: "all", account_id: "all", q: "" });
  const [editing, setEditing] = useState(null); // movement or null
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const { data: categories = [] } = useQuery({ queryKey: ["admin-categories"], queryFn: async () => (await api.get("/administration/categories")).data });
  const visibleCategories = useMemo(() => categories.filter((c) => !c.is_transfer_system), [categories]);
  const { data: accounts = [] } = useQuery({ queryKey: ["admin-accounts"], queryFn: async () => (await api.get("/administration/accounts")).data });
  const { data: movements = [], isLoading } = useQuery({
    queryKey: ["admin-movements", filters],
    queryFn: async () => {
      const p = {};
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
      if (filters.type !== "all") p.type = filters.type;
      if (filters.category_id !== "all") p.category_id = filters.category_id;
      if (filters.account_id !== "all") p.account_id = filters.account_id;
      if (filters.q) p.q = filters.q;
      return (await api.get("/administration/movements", { params: p })).data;
    },
  });

  const total = useMemo(() => {
    const inc = movements.filter((m) => m.type === "entrata").reduce((s, m) => s + m.amount, 0);
    const out = movements.filter((m) => m.type === "uscita").reduce((s, m) => s + m.amount, 0);
    return { inc, out, saldo: inc - out };
  }, [movements]);

  const openNew = () => { setEditing({ date: todayISO(), type: "uscita", amount: "", category_id: "", account_id: "", description: "", reference: "", beneficiary: "" }); setDialogOpen(true); };
  const openEdit = (m) => { setEditing({ ...m, amount: String(m.amount) }); setDialogOpen(true); };

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/administration/movements/${id}`)).data,
    onSuccess: () => { toast.success("Movimento eliminato"); qc.invalidateQueries({ queryKey: ["admin-movements"] }); setToDelete(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/administration/report/export.xlsx?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `movimenti_${filters.date_from || "inizio"}_${filters.date_to || "oggi"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="movimenti-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1C352D]">Movimenti</h1>
          <p className="text-sm text-stone-500 mt-1">Registro entrate e uscite con allegati.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadExcel} data-testid="export-xlsx-btn"><Download size={14} className="mr-1"/> Excel</Button>
          <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="new-transfer-btn" className="border-[#1C352D] text-[#1C352D] hover:bg-[#1C352D] hover:text-white"><ArrowLeftRight size={14} className="mr-1"/> Giro fondi</Button>
          <Button onClick={openNew} data-testid="new-movement-btn" className="bg-[#1C352D] hover:bg-[#264A3E]"><Plus size={14} className="mr-1"/> Nuovo movimento</Button>
        </div>
      </div>

      {/* Filtri */}
      <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Da</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">A</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Tipo</Label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="entrata">Entrate</SelectItem>
                <SelectItem value="uscita">Uscite</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Categoria</Label>
            <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {visibleCategories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Cassa</Label>
            <Select value={filters.account_id} onValueChange={(v) => setFilters({ ...filters, account_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Ricerca</Label>
            <div className="relative mt-1">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400"/>
              <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Descrizione, rif., beneficiario…" className="pl-7"/>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabella */}
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex items-center justify-between border-b border-stone-100 text-sm">
          <span className="text-stone-500">{movements.length} movimenti · Entrate: <b className="text-emerald-700">{euro(total.inc)}</b> · Uscite: <b className="text-red-700">{euro(total.out)}</b></span>
          <span className={`font-semibold ${total.saldo >= 0 ? "text-emerald-700" : "text-red-700"}`}>Saldo periodo: {euro(total.saldo)}</span>
        </div>
        {isLoading ? (
          <div className="p-10 text-center text-stone-400">Caricamento…</div>
        ) : movements.length === 0 ? (
          <div className="p-10 text-center text-stone-400">Nessun movimento con i filtri correnti.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Categoria</th>
                  <th className="text-left px-4 py-3">Descrizione</th>
                  <th className="text-left px-4 py-3">Cassa</th>
                  <th className="text-right px-4 py-3">Importo</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {movements.map((m) => (
                  <tr key={m.id} className={`hover:bg-stone-50 ${m.transfer_id ? "bg-stone-50/60" : ""}`} data-testid={`movement-row-${m.id}`}>
                    <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">{fmtDate(m.date)}</td>
                    <td className="px-4 py-3">
                      {m.transfer_id ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-stone-700 bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5">
                          <ArrowLeftRight size={11}/> Giro fondi
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className="w-2 h-2 rounded-full" style={{ background: m.category_color || "#94a3b8" }}/>
                          {m.category_name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.description || "—"}</div>
                      <div className="text-[11px] text-stone-500 flex gap-2 flex-wrap">
                        {m.beneficiary && <span>{m.beneficiary}</span>}
                        {m.reference && <span className="mono">#{m.reference}</span>}
                        {m.attachment_url && <a href={`${API_BASE}${m.attachment_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[#1C352D] hover:text-[#E2725B]"><Paperclip size={10}/> allegato</a>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">{m.account_name}</td>
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${m.type === "entrata" ? "text-emerald-700" : "text-red-700"}`}>
                      {m.type === "entrata" ? "+" : "−"} {euro(m.amount)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(m)} className="text-stone-500 hover:text-[#1C352D] p-1" data-testid={`edit-${m.id}`}><Pencil size={13}/></button>
                      <button onClick={() => setToDelete(m)} className="text-stone-500 hover:text-red-700 p-1 ml-1" data-testid={`delete-${m.id}`}><Trash2 size={13}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dialogOpen && (
        <MovementDialog
          movement={editing}
          categories={visibleCategories}
          accounts={accounts}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["admin-movements"] }); qc.invalidateQueries({ queryKey: ["admin-summary"] }); }}
        />
      )}

      {transferOpen && (
        <TransferDialog
          accounts={accounts}
          onClose={() => setTransferOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-movements"] });
            qc.invalidateQueries({ queryKey: ["admin-accounts"] });
            qc.invalidateQueries({ queryKey: ["admin-summary"] });
          }}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina movimento?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMut.mutate(toDelete?.id)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MovementDialog({ movement, categories, accounts, onClose, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(movement);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEdit = !!movement?.id;

  const availableCategories = useMemo(() => categories.filter((c) => c.kind === form.type), [categories, form.type]);

  const save = async () => {
    if (!form.date || !form.type || !form.category_id || !form.account_id || !form.amount) {
      toast.error("Compila i campi obbligatori"); return;
    }
    const amt = parseFloat(String(form.amount).replace(",", "."));
    if (isNaN(amt) || amt <= 0) { toast.error("Importo non valido"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: amt };
      delete payload.id; delete payload.created_at; delete payload.created_by; delete payload.updated_at;
      delete payload.category_name; delete payload.category_color; delete payload.account_name; delete payload.account_color;
      const res = isEdit
        ? await api.patch(`/administration/movements/${movement.id}`, payload)
        : await api.post(`/administration/movements`, payload);
      const saved = res.data;

      if (file) {
        const b64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result).split(",")[1]);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        await api.post(`/administration/movements/${saved.id}/attachment`, {
          filename: file.name, content_type: file.type || "application/octet-stream", data_base64: b64,
        });
      }
      toast.success(isEdit ? "Movimento aggiornato" : "Movimento creato");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-stone-100 shrink-0">
          <DialogTitle>{isEdit ? "Modifica movimento" : "Nuovo movimento"}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, type: "entrata", category_id: "" })} className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${form.type === "entrata" ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-stone-500 border-stone-200"}`} data-testid="type-entrata">Entrata</button>
            <button type="button" onClick={() => setForm({ ...form, type: "uscita", category_id: "" })} className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${form.type === "uscita" ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-stone-500 border-stone-200"}`} data-testid="type-uscita">Uscita</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="mov-date"/></div>
            <div><Label>Importo (€) *</Label><Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="0,00" data-testid="mov-amount"/></div>
          </div>
          <div>
            <Label>Categoria *</Label>
            <Select value={form.category_id || undefined} onValueChange={(v) => setForm({ ...form, category_id: v })}>
              <SelectTrigger className="mt-1" data-testid="mov-category"><SelectValue placeholder="Seleziona categoria"/></SelectTrigger>
              <SelectContent>
                {availableCategories.length === 0 ? <SelectItem value="__none__" disabled>Nessuna categoria per questo tipo</SelectItem> :
                  availableCategories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cassa *</Label>
            <Select value={form.account_id || undefined} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger className="mt-1" data-testid="mov-account"><SelectValue placeholder="Seleziona cassa"/></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {euro(a.balance)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Beneficiario / Fornitore</Label><Input value={form.beneficiary || ""} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} placeholder="A chi hai pagato / da chi hai ricevuto" className="mt-1" data-testid="mov-beneficiary"/></div>
          <div><Label>Descrizione</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="mov-desc"/></div>
          <div><Label>Riferimento (n° fattura/ricevuta)</Label><Input value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1" data-testid="mov-ref"/></div>
          <div>
            <Label>Allegato (PDF/immagine, max 15MB)</Label>
            {form.attachment_url && !file && (
              <div className="mt-1 flex items-center gap-2 text-xs text-stone-600 bg-stone-50 border rounded-md px-3 py-2">
                <FileText size={13}/> <span className="flex-1 truncate">{form.attachment_name}</span>
                <a href={`${API_BASE}${form.attachment_url}`} target="_blank" rel="noopener noreferrer" className="text-[#1C352D] hover:underline">apri</a>
              </div>
            )}
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="mov-file"/>
            {file && <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-1">📎 {file.name} <button type="button" onClick={() => setFile(null)}><X size={11}/></button></div>}
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-stone-100 bg-white shrink-0">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="mov-save" className="bg-[#1C352D] hover:bg-[#264A3E]">
            {saving ? "Salvataggio…" : (isEdit ? "Salva" : "Crea movimento")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ accounts, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: todayISO(),
    from_account_id: "",
    to_account_id: "",
    amount: "",
    description: "",
    reference: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.date || !form.from_account_id || !form.to_account_id || !form.amount) {
      toast.error("Compila i campi obbligatori"); return;
    }
    if (form.from_account_id === form.to_account_id) {
      toast.error("Cassa di origine e destinazione devono essere diverse"); return;
    }
    const amt = parseFloat(String(form.amount).replace(",", "."));
    if (isNaN(amt) || amt <= 0) { toast.error("Importo non valido"); return; }
    setSaving(true);
    try {
      await api.post("/administration/transfers", { ...form, amount: amt });
      toast.success("Giro fondi registrato");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const fromAcc = accounts.find((a) => a.id === form.from_account_id);
  const toAcc = accounts.find((a) => a.id === form.to_account_id);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 flex flex-col gap-0" data-testid="transfer-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-stone-100 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight size={16}/> Nuovo giro fondi
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4">
          <div className="text-[12px] text-stone-500 bg-stone-50 border border-stone-200 rounded-md px-3 py-2">
            Un giro fondi sposta denaro tra due casse della scuola. Verranno registrati automaticamente due movimenti collegati (uscita + entrata) con la categoria «Giro fondi».
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="transfer-date"/>
            </div>
            <div>
              <Label>Importo (€) *</Label>
              <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="0,00" data-testid="transfer-amount"/>
            </div>
          </div>
          <div>
            <Label>Da cassa *</Label>
            <Select value={form.from_account_id || undefined} onValueChange={(v) => setForm({ ...form, from_account_id: v })}>
              <SelectTrigger className="mt-1" data-testid="transfer-from"><SelectValue placeholder="Seleziona cassa di origine"/></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {euro(a.balance)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-center -my-1">
            <div className="w-9 h-9 rounded-full bg-stone-100 border border-stone-200 grid place-items-center text-stone-500">
              <ArrowLeftRight size={14}/>
            </div>
          </div>
          <div>
            <Label>A cassa *</Label>
            <Select value={form.to_account_id || undefined} onValueChange={(v) => setForm({ ...form, to_account_id: v })}>
              <SelectTrigger className="mt-1" data-testid="transfer-to"><SelectValue placeholder="Seleziona cassa di destinazione"/></SelectTrigger>
              <SelectContent>
                {accounts.filter((a) => a.id !== form.from_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {euro(a.balance)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Descrizione / Causale</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" placeholder="Es. Prelievo da banchetto per cassa B" data-testid="transfer-desc"/>
          </div>
          <div>
            <Label>Riferimento</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1" data-testid="transfer-ref"/>
          </div>
          {fromAcc && toAcc && form.amount && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs text-emerald-900">
              <b>{fromAcc.name}</b> → <b>{toAcc.name}</b>: {euro(parseFloat(String(form.amount).replace(",", ".")) || 0)}
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-stone-100 bg-white shrink-0">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="transfer-save" className="bg-[#1C352D] hover:bg-[#264A3E]">
            {saving ? "Salvataggio…" : "Registra giro fondi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

